import type { ChatMessage, MailDocumentPayload, RenewOrdonnanceExtraction } from '../types/chat'
import { chatContentTrim } from './chatContent'
import {
  buildSportsCertificatePreviewUrlFromToken,
  buildVoiceNavUrl,
  coerceSportsCertificatePreviewUrl,
  getDefaultVoiceNavApiBaseFromCatalog,
} from '../api/apiConfig'

export type RenewOrdonnanceYesNoKind = 'mail_renewed_ordonnance'

export const RENEW_ORDONNANCE_RENEWING_MESSAGE_ID = 'vnw-renew-ordonnance-renewing'
/** Fallback when PDF iframe onLoad does not fire (common with embedded PDF viewers). */
export const RENEW_ORDONNANCE_PREVIEW_SUCCESS_FALLBACK_MS = 1200
/** Delay between success card and mail preview after PDF is shown. */
export const RENEW_ORDONNANCE_MAIL_AFTER_SUCCESS_MS = 3000

export function renewOrdonnanceMailMessageIds(messages: readonly ChatMessage[]): string[] {
  return messages
    .filter(
      (row) =>
        row.role === 'assistant' &&
        row.renewOrdonnanceYesNoPrompt?.kind === 'mail_renewed_ordonnance' &&
        row.mailDocument?.awaitingMailConfirmation,
    )
    .map((row) => row.id)
}

export function messageLooksLikeRenewOrdonnancePreview(m: ChatMessage | undefined): boolean {
  if (!m) return false
  return m.renewOrdonnanceDocumentPreview === true || m.renewOrdonnanceRenewalCompleted === true
}

export function utteranceLooksLikeVidalRenewCommand(text: string): boolean {
  const t = text.trim().toLowerCase()
  if (!/\b(renew|renouvel\w*)\b/.test(t)) return false
  return (
    /\b(this|that|it|vidal|ordonnance|prescription|medication)\b/.test(t) ||
    /\b\d+\s*(months?|mois|days?|jours?)\b/.test(t) ||
    /\b(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s+months?\b/.test(t)
  )
}

export function renewOrdonnanceRenewingMessage(fr: boolean): ChatMessage {
  return {
    id: RENEW_ORDONNANCE_RENEWING_MESSAGE_ID,
    role: 'assistant',
    content: fr ? "Renouvellement de l'ordonnance Vidal en cours…" : 'Renewing the Vidal ordonnance…',
    timestamp: Date.now(),
    renewOrdonnanceRenewing: true,
    renewOrdonnanceFrench: fr,
    omitFromServerChatHistory: true,
  }
}

export function stripRenewOrdonnanceRenewingMessages(messages: readonly ChatMessage[]): ChatMessage[] {
  return messages.filter(
    (m) => !m.renewOrdonnanceRenewing && m.id !== RENEW_ORDONNANCE_RENEWING_MESSAGE_ID,
  )
}

export function normalizeRenewOrdonnanceMailPreview(raw: unknown): MailDocumentPayload | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const mp = raw as Record<string, unknown>
  const recipientName = String(mp.recipientName ?? mp.recipient_name ?? '').trim()
  const mailSubject = String(mp.mailSubject ?? mp.mail_subject ?? '').trim()
  const mailBody = String(mp.mailBody ?? mp.mail_body ?? '').trim()
  const documentType = String(mp.documentType ?? mp.document_type ?? '').trim()
  if (!recipientName && !mailSubject && !mailBody) return undefined
  return {
    currentIntent: 'mail_document',
    documentType: documentType || 'Renewed Vidal ordonnance',
    mailSubject: mailSubject || 'renewed vidal ordonance',
    mailBody: mailBody || 'renewed vidal ordonance enclosed',
    recipientType: String(mp.recipientType ?? mp.recipient_type ?? 'patient').trim() || 'patient',
    recipientName: recipientName || 'Patient',
    awaitingMailConfirmation: true,
  }
}

export type RenewOrdonnanceAssistantFields = {
  renewOrdonnanceRenewing?: boolean
  renewOrdonnanceRenewalCompleted?: boolean
  renewOrdonnanceFrench?: boolean
  renewOrdonnanceYesNoPrompt?: { kind: RenewOrdonnanceYesNoKind }
  mailDocument?: MailDocumentPayload
  prescriptionDocumentPreviewUrl?: string
  autoOpenPrescriptionDocument?: boolean
  renewOrdonnanceDocumentPreview?: boolean
}

function renewOrdonnancePayloadFromMetadata(
  md: Record<string, unknown>,
): Record<string, unknown> | null {
  if (
    md.renew_ordonnance &&
    typeof md.renew_ordonnance === 'object' &&
    !Array.isArray(md.renew_ordonnance)
  ) {
    return md.renew_ordonnance as Record<string, unknown>
  }
  if (String(md.kind ?? '').trim() === 'renew_ordonnance') return md
  return null
}

/**
 * Resolve a browser-openable VoiceNav preview URL for a renewed ordonnance (PDF proxy).
 * Mirrors {@link resolveSportsNocCertificatePreviewUrlFromMetadata}.
 */
export function resolveRenewOrdonnancePreviewUrlFromMetadata(
  md: Record<string, unknown> | null | undefined,
  apiBaseUrl?: string,
): string | undefined {
  if (!md || String(md.kind ?? '').trim() !== 'renew_ordonnance') return undefined
  const ro = renewOrdonnancePayloadFromMetadata(md)
  if (!ro || ro.renewal_completed !== true) return undefined

  const base = (apiBaseUrl || getDefaultVoiceNavApiBaseFromCatalog()).trim()
  if (!base) return undefined

  const previewToken = String(ro.renewed_document_preview_token ?? '').trim()
  if (previewToken) {
    const fromToken = buildSportsCertificatePreviewUrlFromToken(previewToken, base)
    if (fromToken) return fromToken
  }

  const previewUrl = String(
    ro.renewed_document_preview_url ?? ro.renewed_document_url ?? '',
  ).trim()
  if (!previewUrl) return undefined

  const coerced = coerceSportsCertificatePreviewUrl(previewUrl, base)
  if (coerced) return coerced

  if (previewUrl.startsWith('http://') || previewUrl.startsWith('https://')) {
    try {
      const proxy = new URL(buildVoiceNavUrl(base, 'sportsCertificatePreview'))
      proxy.searchParams.set('file_url', previewUrl)
      return proxy.toString()
    } catch {
      return undefined
    }
  }
  return undefined
}

export function renewOrdonnanceFieldsFromAssistantMetadata(
  md: Record<string, unknown> | null,
): RenewOrdonnanceAssistantFields {
  const out: RenewOrdonnanceAssistantFields = {}
  if (!md || md.kind !== 'renew_ordonnance') return out
  const ro =
    md.renew_ordonnance && typeof md.renew_ordonnance === 'object' && !Array.isArray(md.renew_ordonnance)
      ? (md.renew_ordonnance as Record<string, unknown>)
      : null
  if (!ro) return out

  if (ro.renewal_in_progress === true) out.renewOrdonnanceRenewing = true
  if (ro.renewal_completed === true) out.renewOrdonnanceRenewalCompleted = true
  if (ro.french === true) out.renewOrdonnanceFrench = true
  else if (ro.french === false) out.renewOrdonnanceFrench = false

  const previewUrl = resolveRenewOrdonnancePreviewUrlFromMetadata(md)
  const mailDocUrl = String(ro.renewed_document_url ?? '').trim()
  if (out.renewOrdonnanceRenewalCompleted && previewUrl) {
    out.prescriptionDocumentPreviewUrl = previewUrl
    out.autoOpenPrescriptionDocument = true
    out.renewOrdonnanceDocumentPreview = true
  }

  const ynp = ro.yes_no_prompt
  const ynpKind =
    ynp && typeof ynp === 'object' && !Array.isArray(ynp)
      ? String((ynp as Record<string, unknown>).kind ?? '').trim()
      : ''
  const awaitingMail = ro.awaiting_mail_confirmation === true || ro.awaitingMailConfirmation === true
  const mp = normalizeRenewOrdonnanceMailPreview(ro.mail_preview)
  if (mp && (ynpKind === 'mail_renewed_ordonnance' || awaitingMail)) {
    const receiverId = String(ro.mail_receiver_id ?? ro.patient_uuid ?? '').trim()
    out.mailDocument = {
      ...mp,
      mailAttachmentUrl: mailDocUrl || undefined,
      mailReceiverId: receiverId || undefined,
    }
    out.renewOrdonnanceYesNoPrompt = { kind: 'mail_renewed_ordonnance' }
  }
  return out
}

export function isRenewOrdonnanceMailConfirmRow(m: ChatMessage): boolean {
  return (
    m.renewOrdonnanceYesNoPrompt?.kind === 'mail_renewed_ordonnance' &&
    Boolean(m.mailDocument?.awaitingMailConfirmation) &&
    !m.renewOrdonnanceMailResolved
  )
}

type RenewClarificationFor = NonNullable<RenewOrdonnanceExtraction['clarification_for']>

function renewClarificationFor(raw: Record<string, unknown>): RenewClarificationFor | null {
  const cf = String(raw.clarification_for ?? raw.clarificationFor ?? '').trim()
  if (cf === 'prescription_type' || cf === 'patient_uuid' || cf === 'renew_days') return cf
  return null
}

function renewNeedsClarification(raw: Record<string, unknown>): boolean {
  if (raw.needs_clarification === true || raw.needsClarification === true) return true
  return renewClarificationFor(raw) != null
}

const RENEW_CLARIFY_QUESTION_EN: Record<Exclude<RenewClarificationFor, null | undefined>, string> = {
  prescription_type: 'Which prescription would you like to renew?',
  patient_uuid: 'Please open or select a patient first before renewing the ordonnance.',
  renew_days: 'For how many days should it be renewed?',
}

const RENEW_CLARIFY_QUESTION_FR: Record<Exclude<RenewClarificationFor, null | undefined>, string> = {
  prescription_type: 'Quelle ordonnance souhaitez-vous renouveler ?',
  patient_uuid:
    "Veuillez d'abord ouvrir ou sélectionner un patient avant de renouveler l'ordonnance.",
  renew_days: 'Pour combien de jours faut-il la renouveler ?',
}

/** Normalize backend ``renew_ordonnance`` metadata (snake_case booleans and fields). */
export function normalizeRenewOrdonnanceMeta(
  raw: Record<string, unknown>,
): RenewOrdonnanceExtraction {
  const cf = renewClarificationFor(raw)
  const needs = renewNeedsClarification(raw)
  const fr = raw.french === true
  const questionRaw = String(raw.clarification_question ?? raw.clarificationQuestion ?? '').trim()
  const clarificationQuestion =
    questionRaw ||
    (needs && cf ? (fr ? RENEW_CLARIFY_QUESTION_FR[cf] : RENEW_CLARIFY_QUESTION_EN[cf]) : null)
  const renewDaysRaw = raw.renew_days ?? raw.renewDays
  return {
    intent: 'renew_ordonnance',
    patient_uuid:
      raw.patient_uuid != null && String(raw.patient_uuid).trim() !== ''
        ? String(raw.patient_uuid)
        : raw.patientUuid != null && String(raw.patientUuid).trim() !== ''
          ? String(raw.patientUuid)
          : null,
    prescription_type:
      raw.prescription_type != null && String(raw.prescription_type).trim() !== ''
        ? String(raw.prescription_type)
        : raw.prescriptionType != null && String(raw.prescriptionType).trim() !== ''
          ? String(raw.prescriptionType)
          : null,
    renew_days:
      typeof renewDaysRaw === 'number' && Number.isFinite(renewDaysRaw) ? renewDaysRaw : null,
    original_duration_text:
      raw.original_duration_text != null
        ? String(raw.original_duration_text)
        : raw.originalDurationText != null
          ? String(raw.originalDurationText)
          : null,
    needs_clarification: needs,
    clarification_for: cf,
    clarification_question: clarificationQuestion,
    vidal_renewal_ai: raw.vidal_renewal_ai ?? raw.vidalRenewalAi,
    vidal_renewal_ai_error:
      raw.vidal_renewal_ai_error != null
        ? String(raw.vidal_renewal_ai_error)
        : raw.vidalRenewalAiError != null
          ? String(raw.vidalRenewalAiError)
          : null,
  }
}

function renewOrdonnanceSkipsPlainBubble(m: ChatMessage): boolean {
  return Boolean(
    m.renewOrdonnanceRenewing ||
      m.renewOrdonnanceRenewalCompleted ||
      m.renewOrdonnanceYesNoPrompt?.kind === 'mail_renewed_ordonnance' ||
      m.renewOrdonnanceMailResolved ||
      m.mailDocument?.awaitingMailConfirmation,
  )
}

function renewOrdonnanceTechnicalDumpContent(content: string): boolean {
  const c = content.trim()
  if (!c) return false
  return (
    /\brenew_ordonnance\b/.test(c) ||
    /Prescription type\b/i.test(c) ||
    /Type d'ordonnance\b/i.test(c) ||
    /Needs clarification\b/i.test(c) ||
    /Clarification requise\b/i.test(c) ||
    /^Intent\s*:/im.test(c) ||
    /^Intention\b/im.test(c) ||
    /Duration \(spoken\)/i.test(c) ||
    /Durée \(texte\)/i.test(c)
  )
}

export function renewOrdonnanceIsClarificationStep(m: ChatMessage): boolean {
  const meta = m.renewOrdonnanceMeta
  if (!meta || renewOrdonnanceSkipsPlainBubble(m)) return false
  if (meta.needs_clarification === true) return true
  if (String(meta.clarification_question ?? '').trim()) return true
  const cf = String(meta.clarification_for ?? '').trim()
  return cf === 'prescription_type' || cf === 'patient_uuid' || cf === 'renew_days'
}

/** Plain question text for renew_ordonnance clarification bubbles (EN/FR). */
export function renewOrdonnanceClarificationDisplay(m: ChatMessage): string | null {
  if (!renewOrdonnanceIsClarificationStep(m)) return null
  const meta = m.renewOrdonnanceMeta
  if (!meta) return null
  const q = String(meta.clarification_question ?? '').trim()
  if (q) return q
  const cf = meta.clarification_for
  if (!cf) return null
  const fr = renewOrdonnanceUiFr(m)
  return fr ? RENEW_CLARIFY_QUESTION_FR[cf] : RENEW_CLARIFY_QUESTION_EN[cf]
}

/**
 * User-visible renew_ordonnance bubble before mail/success UI — clarification question only,
 * never the technical intent metadata block.
 */
export function renewOrdonnanceAssistantBubbleText(m: ChatMessage): string | null {
  const meta = m.renewOrdonnanceMeta
  if (!meta || renewOrdonnanceSkipsPlainBubble(m)) return null

  const clarify = renewOrdonnanceClarificationDisplay(m)
  if (clarify) return clarify

  const q = String(meta.clarification_question ?? '').trim()
  if (q) return q

  const content = chatContentTrim(m.content)
  if (content && renewOrdonnanceTechnicalDumpContent(content)) {
    const cf = meta.clarification_for
    if (cf) {
      const fr = renewOrdonnanceUiFr(m)
      return fr ? RENEW_CLARIFY_QUESTION_FR[cf] : RENEW_CLARIFY_QUESTION_EN[cf]
    }
    const lines = content
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
    const last = lines[lines.length - 1]
    if (last && !renewOrdonnanceTechnicalDumpContent(last)) return last
    return null
  }

  const err = String(meta.vidal_renewal_ai_error ?? '').trim()
  if (err) return err

  return content || null
}

/** @deprecated Structured renew_ordonnance metadata panel removed — always false. */
export function shouldShowRenewOrdonnanceStructuredPreview(_m: ChatMessage): boolean {
  return false
}

export function renewOrdonnanceMailConfirmUiReady(
  m: ChatMessage,
  unlockedMessageIds: ReadonlySet<string>,
): boolean {
  if (m.renewOrdonnanceYesNoPrompt?.kind !== 'mail_renewed_ordonnance') return true
  return unlockedMessageIds.has(m.id)
}

/** Latest renew success row + mail confirm ids when staged reveal should run. */
export function findRenewOrdonnanceStagedRevealTargets(
  messages: readonly ChatMessage[],
): { successMessageId: string; mailMessageIds: string[] } | null {
  let successMessageId: string | null = null
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.historySnapshot || m.role !== 'assistant') continue
    if (m.renewOrdonnanceRenewalCompleted) {
      successMessageId = m.id
      break
    }
  }
  if (!successMessageId) return null
  const mailMessageIds = renewOrdonnanceMailMessageIds(messages)
  const successMsg = messages.find((m) => m.id === successMessageId)
  const needsStagedReveal =
    successMsg?.renewOrdonnanceDocumentPreview === true || mailMessageIds.length > 0
  if (!needsStagedReveal) return null
  return { successMessageId, mailMessageIds }
}

/** Success card waits until the renewed PDF preview has loaded (or history replay). */
export function renewOrdonnanceSuccessUiReady(
  m: ChatMessage,
  unlockedMessageIds: ReadonlySet<string>,
  historySnapshot = false,
): boolean {
  if (!m.renewOrdonnanceRenewalCompleted) return false
  if (historySnapshot) return true
  if (!m.renewOrdonnanceDocumentPreview) return true
  return unlockedMessageIds.has(m.id)
}

export function renewOrdonnanceUiFr(m?: ChatMessage): boolean {
  if (m?.renewOrdonnanceFrench === true) return true
  if (m?.renewOrdonnanceFrench === false) return false
  return false
}
